
import React from 'react';
import { Brain, TrendingUp } from 'lucide-react';

interface BehavioralData {
  dominance: { score: number; percentile: number };
  extraversion: { score: number; percentile: number };
  patience: { score: number; percentile: number };
  formality: { score: number; percentile: number };
  objectivity: { score: number; percentile: number };
}

interface CognitiveData {
  overall_percentile: number;
  verbal_score: number;
  numerical_score: number;
  abstract_score: number;
}

interface MetricsOverviewProps {
  behavioralData: BehavioralData;
  cognitiveData: CognitiveData | null;
  loadingCognitive: boolean;
}

const MetricsOverview: React.FC<MetricsOverviewProps> = ({ 
  behavioralData, 
  cognitiveData, 
  loadingCognitive 
}) => {
  const getPerformanceLabel = (percentile: number) => {
    if (percentile >= 85) return 'Exceptional';
    if (percentile >= 70) return 'Strong';
    if (percentile >= 55) return 'Good';
    if (percentile >= 40) return 'Developing';
    return 'Growing';
  };

  const primaryTraits = [
    { name: 'Drive', value: behavioralData.dominance.percentile, color: '#1D1D1F' },
    { name: 'Connection', value: behavioralData.extraversion.percentile, color: '#1D1D1F' },
    { name: 'Task', value: behavioralData.patience.percentile, color: '#1D1D1F' },
    { name: 'Order', value: behavioralData.formality.percentile, color: '#1D1D1F' }
  ];

  return (
    <div className="bg-white rounded-2xl p-8 border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-8">
        <TrendingUp className="h-6 w-6 text-[#86868B]" />
        <h2 className="text-2xl font-light text-[#1D1D1F]">Key Metrics</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Behavioral Metrics */}
        <div>
          <h3 className="text-lg font-medium text-[#1D1D1F] mb-6">Behavioral Profile</h3>
          <div className="grid grid-cols-2 gap-6">
            {primaryTraits.map((trait) => (
              <div key={trait.name} className="text-center">
                <div className="text-3xl font-light text-[#1D1D1F] mb-2">
                  {trait.value}%
                </div>
                <div className="text-sm font-medium text-[#86868B]">
                  {trait.name}
                </div>
                <div className="w-full bg-[#E5E5EA] rounded-full h-1 mt-3">
                  <div 
                    className="h-1 rounded-full bg-[#1D1D1F] transition-all duration-1000"
                    style={{ width: `${trait.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Style Factor */}
          <div className="mt-8 pt-6 border-t border-[#E5E5EA]">
            <div className="text-center">
              <div className="text-2xl font-light text-[#1D1D1F] mb-2">
                {behavioralData.objectivity.percentile}%
              </div>
              <div className="text-sm font-medium text-[#86868B] mb-3">
                Style Factor
              </div>
              <div className="flex items-center justify-between text-xs text-[#86868B] mb-2">
                <span>Subjective</span>
                <span>Objective</span>
              </div>
              <div className="w-full bg-[#E5E5EA] rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-[#86868B] transition-all duration-1000"
                  style={{ width: `${behavioralData.objectivity.percentile}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Cognitive Metrics */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Brain className="h-5 w-5 text-[#86868B]" />
            <h3 className="text-lg font-medium text-[#1D1D1F]">Cognitive Performance</h3>
          </div>
          
          {loadingCognitive ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-[#86868B]">Loading cognitive data...</div>
            </div>
          ) : cognitiveData ? (
            <div className="space-y-6">
              {/* Overall Performance */}
              <div className="text-center p-6 bg-[#F5F5F7] rounded-xl">
                <div className="text-4xl font-light text-[#1D1D1F] mb-2">
                  {cognitiveData.overall_percentile}%
                </div>
                <div className="text-lg font-medium text-[#1D1D1F] mb-1">
                  {getPerformanceLabel(cognitiveData.overall_percentile)}
                </div>
                <div className="text-sm text-[#86868B]">
                  Overall Cognitive Percentile
                </div>
              </div>

              {/* Breakdown */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-xl font-light text-[#1D1D1F]">
                    {cognitiveData.verbal_score}%
                  </div>
                  <div className="text-xs text-[#86868B] mt-1">Verbal</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-light text-[#1D1D1F]">
                    {cognitiveData.numerical_score}%
                  </div>
                  <div className="text-xs text-[#86868B] mt-1">Numerical</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-light text-[#1D1D1F]">
                    {cognitiveData.abstract_score}%
                  </div>
                  <div className="text-xs text-[#86868B] mt-1">Abstract</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-[#86868B]">
              <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <div className="text-lg font-medium mb-2">No Cognitive Assessment</div>
              <div className="text-sm">Cognitive assessment data not available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricsOverview;
